/**
 * 通用「重连管理器」
 *
 * 用一份注册表（数组）统一管理所有需要在以下场景重新连接 / 重新拉取的资源：
 *  1. 浏览器网络恢复（window 'online' 事件）
 *  2. 页面恢复可见（document 'visibilitychange' → visible）
 *  3. 心跳/活跃度超时（每项可独立配置 heartbeatTimeoutMs）
 *
 * 适用场景：
 *  - SSE 连接（fetch-stream 实现，不能依赖原生 EventSource 自动重连）
 *  - WebSocket 连接
 *  - 长轮询接口、关键业务接口（恢复后立即重新拉取）
 *
 * 使用示例：
 *   import { reconnectManager } from '@/utils/reconnectManager'
 *
 *   // 注册一项
 *   reconnectManager.register({
 *     id: 'java-sse',
 *     type: 'sse',
 *     reconnect: () => store.dispatch(connectSse()),
 *     heartbeatTimeoutMs: 90 * 1000,
 *   })
 *
 *   // 收到心跳/数据时刷新活跃时间，避免被看门狗误判
 *   reconnectManager.touch('java-sse')
 *
 *   // 卸载时取消注册
 *   reconnectManager.unregister('java-sse')
 *
 *   // 手动重连
 *   reconnectManager.reconnectOne('java-sse', '手动触发')
 *   reconnectManager.reconnectAll('全局刷新')
 */

const DEFAULT_HEARTBEAT_TIMEOUT_MS = 90 * 1000
const DEFAULT_HEARTBEAT_CHECK_INTERVAL_MS = 15 * 1000
// 同一项在 minReconnectIntervalMs 内只允许触发一次重连（防抖）
const DEFAULT_MIN_RECONNECT_INTERVAL_MS = 3 * 1000

class ReconnectManager {
  constructor() {
    /**
     * 注册表（数组）
     * 每项形如：
     * {
     *   id: string,                        // 唯一标识
     *   type: 'sse' | 'ws' | 'api' | 'custom',
     *   reconnect: () => Promise|void,     // 必填：执行重连的函数
     *   shouldReconnect?: () => boolean,   // 可选：返回 false 则跳过该项
     *   heartbeatTimeoutMs?: number,       // 可选：心跳超时阈值（默认 90s）
     *   heartbeatEnabled?: boolean,        // 可选：默认 true。false 时不跑 watchdog / visibility 超时 / soft-online（仅靠显式 reconnect / refetchAfterJavaSseReconnect）
     *   minReconnectIntervalMs?: number,   // 可选：最小重连间隔（默认 3s）
     *   reconnectOnOnline?: boolean,        // 可选：默认 true。false 时 online 仅当「距上次活跃 > heartbeatTimeoutMs」才重连（适合 HTTP，避免误报 online 刷屏）
     *   refetchAfterJavaSseReconnect?: boolean, // 可选：默认 false。为 true 时由 sseSlice 在「Java SSE 非首次成功 onOpen」后调用 refetchAfterJavaSseReconnect() 强制拉取（服务端重启后与 SSE 对齐）
     *   onReconnect?: (reason) => void,    // 可选：每次触发重连时的回调（用于日志/埋点）
     *   meta?: Object,                     // 可选：附加信息
     *   // 运行期内部字段（外部不要直接读写）：
     *   _lastActiveAt: number,             // 最近一次 touch / reconnect 的时间戳
     *   _lastReconnectAt: number,          // 最近一次成功触发 reconnect 的时间戳
     * }
     */
    this.registry = []

    this._watchdogTimer = null
    this._lifecycleBound = false
    this._onlineHandler = null
    this._visibilityHandler = null
  }

  /**
   * 注册一项需要被重连管理的资源
   * 重复注册同一 id 会先注销旧的
   * @param {Object} item
   * @returns {Function} 取消注册的函数
   */
  register(item) {
    if (!item || !item.id || typeof item.reconnect !== 'function') {
      console.warn('[ReconnectManager] 注册失败：必须提供 id 与 reconnect 函数', item)
      return () => {}
    }

    if (this.registry.find((it) => it.id === item.id)) {
      this.unregister(item.id)
    }

    const now = Date.now()
    this.registry.push({
      type: 'custom',
      heartbeatTimeoutMs: DEFAULT_HEARTBEAT_TIMEOUT_MS,
      heartbeatEnabled: true,
      minReconnectIntervalMs: DEFAULT_MIN_RECONNECT_INTERVAL_MS,
      reconnectOnOnline: true,
      ...item,
      _lastActiveAt: now,
      _lastReconnectAt: 0,
    })

    this._ensureLifecycle()
    this._ensureWatchdog()

    return () => this.unregister(item.id)
  }

  /**
   * 取消注册
   */
  unregister(id) {
    const idx = this.registry.findIndex((it) => it.id === id)
    if (idx >= 0) this.registry.splice(idx, 1)
    if (this.registry.length === 0) {
      this._teardown()
    }
  }

  /**
   * 取消所有注册
   */
  unregisterAll() {
    this.registry = []
    this._teardown()
  }

  /**
   * 刷新某项的"最近活跃时间"
   * 例如 SSE 收到 heartbeat / message 时调用，避免被心跳看门狗误判为断线
   */
  touch(id) {
    const item = this.registry.find((it) => it.id === id)
    if (item) item._lastActiveAt = Date.now()
  }

  /**
   * 触发某一项重连
   * @param {string} id
   * @param {string} reason 仅用于日志
   */
  reconnectOne(id, reason = '') {
    const item = this.registry.find((it) => it.id === id)
    if (!item) return false
    return this._doReconnect(item, reason)
  }

  /**
   * 触发所有项重连
   */
  reconnectAll(reason = '') {
    this.registry.forEach((item) => this._doReconnect(item, reason))
  }

  /**
   * Java SSE 再次成功建连后调用（页面生命周期内第二次及以后的 onOpen）。
   * 对 `refetchAfterJavaSseReconnect: true` 的注册项执行 reconnect，用于服务端重启/断连恢复后
   * 重新拉 HTTP 基线，与已恢复的 SSE 增量对齐。
   */
  refetchAfterJavaSseReconnect(reason = 'Java SSE 重连成功') {
    this.registry.forEach((item) => {
      if (item.refetchAfterJavaSseReconnect === true) {
        this._doReconnect(item, reason)
      }
    })
  }

  /**
   * 列出当前注册项（只暴露公开字段）
   */
  list() {
    return this.registry.map(({ _lastActiveAt, _lastReconnectAt, ...rest }) => ({
      ...rest,
      lastActiveAt: _lastActiveAt,
      lastReconnectAt: _lastReconnectAt,
    }))
  }

  // ============== 内部方法 ==============

  _doReconnect(item, reason) {
    if (typeof item.shouldReconnect === 'function') {
      try {
        if (!item.shouldReconnect()) return false
      } catch (e) {
        // shouldReconnect 出错时，保守起见仍然允许重连
      }
    }

    const now = Date.now()
    const minInterval = item.minReconnectIntervalMs || DEFAULT_MIN_RECONNECT_INTERVAL_MS
    if (now - item._lastReconnectAt < minInterval) {
      return false
    }
    item._lastReconnectAt = now
    item._lastActiveAt = now

    try {
      if (typeof item.onReconnect === 'function') {
        try { item.onReconnect(reason) } catch (e) {}
      }
      console.log(`[ReconnectManager] 触发重连 [${item.id}] ${reason ? `(${reason})` : ''}`)
      const ret = item.reconnect(reason)
      if (ret && typeof ret.catch === 'function') {
        ret.catch((err) => {
          console.warn(`[ReconnectManager] [${item.id}] reconnect 失败:`, err)
        })
      }
      return true
    } catch (err) {
      console.warn(`[ReconnectManager] [${item.id}] reconnect 抛错:`, err)
      return false
    }
  }

  _ensureLifecycle() {
    if (this._lifecycleBound) return
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    this._onlineHandler = () => {
      const now = Date.now()
      this.registry.forEach((item) => {
        // 仅吃显式重连信号的项（如监控墙列表）：不因 online 误刷
        if (item.heartbeatEnabled === false) return
        const softOnline = item.reconnectOnOnline === false
        if (softOnline) {
          const timeout = item.heartbeatTimeoutMs || DEFAULT_HEARTBEAT_TIMEOUT_MS
          if (now - item._lastActiveAt > timeout) {
            this._doReconnect(item, '网络已恢复且数据已过期')
          }
          return
        }
        this._doReconnect(item, '网络已恢复')
      })
    }
    this._visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return
      // 页面恢复可见时，仅对"心跳已超时"的项进行重连，避免来回切标签也疯狂重建
      const now = Date.now()
      this.registry.forEach((item) => {
        if (item.heartbeatEnabled === false) return
        const timeout = item.heartbeatTimeoutMs || DEFAULT_HEARTBEAT_TIMEOUT_MS
        if (now - item._lastActiveAt > timeout) {
          this._doReconnect(item, '页面恢复可见且心跳超时')
        }
      })
    }
    window.addEventListener('online', this._onlineHandler)
    document.addEventListener('visibilitychange', this._visibilityHandler)
    this._lifecycleBound = true
  }

  _ensureWatchdog() {
    if (this._watchdogTimer) return
    this._watchdogTimer = setInterval(() => {
      if (this.registry.length === 0) return
      const now = Date.now()
      this.registry.forEach((item) => {
        if (item.heartbeatEnabled === false) return
        const timeout = item.heartbeatTimeoutMs || DEFAULT_HEARTBEAT_TIMEOUT_MS
        const lastActiveAgo = now - item._lastActiveAt
        if (lastActiveAgo > timeout) {
          console.warn(
            `[ReconnectManager] watchdog 超时触发重连 [${item.id}] ` +
            `距上次活跃 ${Math.round(lastActiveAgo / 1000)}s, 阈值 ${Math.round(timeout / 1000)}s`
          )
          this._doReconnect(item, `心跳超时 ${Math.round(lastActiveAgo / 1000)}s`)
        }
      })
    }, DEFAULT_HEARTBEAT_CHECK_INTERVAL_MS)
  }

  _teardown() {
    if (this._watchdogTimer) {
      clearInterval(this._watchdogTimer)
      this._watchdogTimer = null
    }
    if (this._lifecycleBound) {
      if (typeof window !== 'undefined' && this._onlineHandler) {
        window.removeEventListener('online', this._onlineHandler)
      }
      if (typeof document !== 'undefined' && this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler)
      }
      this._onlineHandler = null
      this._visibilityHandler = null
      this._lifecycleBound = false
    }
  }
}

// 全局单例
export const reconnectManager = new ReconnectManager()

export default reconnectManager
